import React from 'react';
import { Card, Form, Input, Typography } from 'antd';
import { ExportOutlined, HomeOutlined, ImportOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { type TransportKind } from '../modules/transports/transportDirection';
import TransportFormSectionTitle from './TransportFormSectionTitle';
import './TransportKindPicker.css';

const { Text } = Typography;

interface TransportKindPickerProps {
  kind: TransportKind | null;
  onChange: (kind: TransportKind) => void;
  /** Kinds that cannot be chosen now, with the Polish reason (edit mode). */
  disabledReasons?: Partial<Record<TransportKind, string>>;
  /** Customer orders on the truck — names where factory goods go now. */
  customerOrderCount?: number;
}

/**
 * Owner 2026-10-12: three tiles, one per truck kind — „Z fabryki do
 * klientów”, „Z fabryki na magazyn”, „Z naszego magazynu”. Customer pickup
 * is not a truck and has no tile. The kinds, the direction payload and the
 * money rule stay unchanged.
 */
const TransportKindPicker: React.FC<TransportKindPickerProps> = ({
  kind, onChange, disabledReasons = {}, customerOrderCount = 0,
}) => {
  const { t } = useTranslation(['transport']);
  const tiles: Array<{ value: TransportKind; icon: React.ReactNode }> = [
    { value: 'FACTORY_TO_CUSTOMER', icon: <ImportOutlined /> },
    { value: 'FACTORY_TO_WAREHOUSE', icon: <InboxOutlined /> },
    { value: 'WAREHOUSE_TO_CUSTOMER', icon: <ExportOutlined /> },
  ];

  return (
    <Card
      className="transport-form-section transport-kind-picker"
      data-testid="transport-kind-picker"
      styles={{ body: { padding: '12px 16px 16px' } }}
    >
      <TransportFormSectionTitle icon={<HomeOutlined />} label={t('transport:kind.stepTitle')} />
      {!kind ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>{t('transport:kind.stepHint')}</Text>
      ) : null}
      <div className="transport-kind-picker__tiles" role="radiogroup" aria-label={t('transport:kind.label')}>
        {tiles.map(({ value, icon }) => {
          const selected = value === kind;
          const disabledReason = disabledReasons[value];
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={Boolean(disabledReason)}
              title={disabledReason}
              data-testid={`transport-kind-option-${value}`}
              className={`transport-kind-picker__tile${selected ? ' is-selected' : ''}`}
              onClick={() => { if (!selected) onChange(value); }}
            >
              <span className="transport-kind-picker__icon">{icon}</span>
              <span className="transport-kind-picker__text">
                {/* Owner 2026-10-12: the kind is the headline; where to start is a hint. */}
                <strong className="transport-kind-picker__title">{t(`transport:kind.start.${value}.question`)}</strong>
                <span className="transport-kind-picker__route">{t(`transport:kind.options.${value}.label`)}</span>
                <small className="transport-kind-picker__start">{t(`transport:kind.start.${value}.label`)}</small>
                <small>{t(`transport:kind.start.${value}.description`)}</small>
                {selected && value === 'FACTORY_TO_CUSTOMER' && customerOrderCount > 0 ? (
                  <small className="transport-kind-picker__now" data-testid="transport-kind-factory-now">
                    {t('transport:kind.start.FACTORY_TO_CUSTOMER.now', { count: customerOrderCount })}
                  </small>
                ) : null}
                {disabledReason ? <small className="transport-kind-picker__reason">{disabledReason}</small> : null}
              </span>
            </button>
          );
        })}
      </div>
      {/* The kind's direction travels as before (payload `direction`). */}
      <Form.Item name="direction" hidden noStyle>
        <Input />
      </Form.Item>
    </Card>
  );
};

export default TransportKindPicker;
